// SYSTEM

import { ParameterInstanceCreateContainer } from "../interfaces/entity-settings/parameters/parameters";
import { ParameterInstanceContainer } from "../interfaces/parameters/parameter-models";
import { V2ParameterValueType } from "./parameter-types";

// enumerable type for variable data type selectors
export enum VariableType {
    Entity = 1,
    Date = 2,
    DateTime = 3,
    DateRange = 4,
    Number = 5,
    String = 6,
    Boolean = 7,
    Enum = 8,
    EntityInside = 9,
    ParameterInline = 10,
    Bill = 11,
    Counterparty = 12,
    AddressInline = 13,
}

export interface AuxSelectorFilter {
    stringFilter: string;
    objFilter?: any;
}

// COMMON

export enum ParameterInstanceContainerType {
    // Единичный параметр.
    SingleInstance = 0,
    // Список параметров.
    List = 1
}

export enum SelectorDataType {
    List = 1,
    Tree = 2
}

export enum AnalyticsV2ElementType {
    Project = 0,
    Client = 1,
    Job = 2,
    Person = 3,
    File = 4,
    Tag = 5,
    Workload = 6,

    StorageItem = 7,
    StorageItemMovement = 8,

    // superficial indices
    Report = 900
}

export enum TagType {
    // Стандартный тег.
    Standard = 0,
    // Скрытый тег.
    Hidden = 1
}

export enum AnalyticsReportElementAddType {
    TableElement = 0,
    Chart = 1
}

export enum AnalyticsReportElementType {
    Selection = 0, 
    Projection = 1, 
    Chart = 2, 
    Break = 3,
    List = 4, 
}

// Маштаб элемента, отображаемого в отчете аналитики.
export enum AnalyticsReportElementScaleV2 {
    // Целый экран, 1х.
    FullScreen = 0,
    // Половина экрана, 1/2х.
    HalfScreen = 1,
    // Четверть экрана, 1/4х.
    QuarterScreen = 2
}

export enum AnalyticsV2ChartType {
    Linear = 0,    
    Pie = 1,    
    Bar = 2
}

// TAGS

export enum TagEntityType {
    Unknown = -1,
    Job = 0,
    Person = 1,
    Client = 2,
    Project = 3,
    ClientFilter = 4,
    ProjectFilter = 5,
    File = 6,
    Folder = 7
}

export enum TagBaseType {
    Common = 0,
    Hidden = 1
}

// ERRORS

export enum ServerExceptionType {
    Unknown = 0,
    _1C = 1,
    Project = 2,
    Analytics = 3
}

export enum ProjectExceptionType {
    Unknown = 0,
    NotFound = 1,
    Removed = 2
}

export enum PaymentExceptionType {
    Unknown = 0,
    NotFound = 1,
    Removed = 2,

    InvalidSum = 3,
    InvalidDate = 4,
}

export enum AnalyticsExceptionType {
    Unknown = 0,
    ParentNotFound = 1,
    ParentRemoved = 2,
    ParentInvalid = 3
}

// COLUMNS

export enum AnalyticsV2ElementWidth {
    // По ширине экрана
    ScreenSize = 0,
    // Полтора
    OneAndAHalf = 1,
    // Два
    Two = 2,
    // Два с половиной
    TwoAndAHalf = 3,
    // Три
    Three = 4,
    // x0.25 scale.
    Quarter = 5,
    // x0.5 scale.
    Half = 6,
    // x0.75 scale.
    ThreeQuarters = 7,
    // x1.25 scale.
    OneAndQuarter = 8,
    // x1.75 scale.
    OneAndThreeQuarters = 9,
}

export enum AnalyticsChartElementWidth {
    // Default x1 scale.
    Default = 0,

    // x0.5 scale.
    Half = 1,

    // x0.75 scale.
    ThreeQuarters = 2,

    // x1.25 scale.
    OneAndQuarter = 3,

    // x1.5 scale.
    OneAndHalf = 4,

    // x1.75 scale.
    OneAndThreeQuarters = 5,

    // x2 scale.
    Two = 6
}

export enum ColumnSendTypeDescriptor {
    Selectable = 0,// обычная колонка без опций, может быть с entity.
    SelectableWithOptions = 1,//  колонка с доп опциями - toggl'ами
    Composite = 2,//  колонка - категория, внутри нее - параметры / роли клиента /  пр.
    DateRange = 3, // колонка с настройками диапазона дат.
    SpecialRules = 15
}

export enum ColumnCategory {
    Name = 0,
    Tags = 1,
    Stage = 2,
    Clients = 3,
    Address = 4,
    AttachedWorkers = 5,
    Duration = 6,
    LastJobDate = 7,
    LastStageChangeDate = 8,
    JobCount = 9,
    WorkerInJobCount = 10,
    CreateDate = 11,
    Parameters = 12,
    Union = 13,
    ProjectCount = 14,
    Status = 15,
    JobFinishDateTime = 16,
    Creator = 17,
    JobText = 18,
    EntityOfAttachment = 19,
    PersonPost = 20,
    PersonDetails = 21,
    Projects = 22,
    LastChangeDate = 23,
    ClientCount = 24,
    FileCount = 25,
    PersonCount = 26,
    GeneralCount = 27,
    IpAddress = 28,

    ItemVendorCode = 29,
    StoragesWithItem = 30,
    TotalAmount = 31,
    Bills = 32,
    SingleItemPrice = 33,
    Incomes = 34,
    Expenses = 35,
    Transfers = 36,
    FinancialBurden = 37,
    Notes = 38,
    LastMovementDate = 39,
    MovementType = 40,
    ResponsibleWorker = 41,
    SourceStorage = 42,
    ReceiverStorage = 43,
    Files = 44,
    StorageItemNameParameters = 45,
    InitialProjectionElements = 46,
    MovementVendorCode = 47,
    Owner = 48,
    SpecialRules = 49
}

export enum ColumnSendToggleArrayMode {
    // Стандартное поведение.
    Common = 0,
    // Хотя бы один элемент должен быть выбран.
    NonEmpty = 1
}

export enum ColumnToggleArrayType {
    ShowUnionClosely = 0,
    JobStatusDisplay = 1,
    AddressLevelsShown = 2,
    AddressRoles = 3,
    PersonLastChangeOptions = 4,
    ShowWorkersInJob = 5
}

export enum ColumnToggleType {
    Entity = 0,
    ShowUnionClosely = 1,
    StatusAsColor = 2,
    StatusAsText = 3,
    AllAddressLevels = 4,
    AnyRole = 5,
    ShowLastChanger = 6,
    ShowWorkersInJob = 7,
    ShowChangesCount = 8
}

// FILTERS

export enum SelectionFilterGroupSendDescriptor {
    Text = 0,
    EntityWithRoles = 1,
    Datestamps = 2,
    ProjectStages = 3,
    Parameters = 4,
    JobStatus = 5,
    FileAttachment = 6,
    // ToggleDatestamps = 7,
    TagStatus = 7,

    Numbers = 8,
    StoragesOfItem = 9,
    StoragesOfMovement = 10,
    MovementTypes = 11,
    StorageItemNames = 12,
    ProjectionRoles = 13,
    LastMovementOfStorageItem = 14,
    SpecialRules = 15
}

export enum SelectionFilterGroupSendCategory {
    Name = 0,
    Tags = 1,
    ProjectStage = 2,
    Workers = 3,
    Clients = 4,
    Projects = 5,
    Address = 6,
    CreateDate = 7,
    JobStatus = 8,
    JobText = 9,
    PersonPost = 10,
    PersonDetails = 11,
    Creator = 12,
    LastChangeDate = 13,
    FileAttachment = 14,
    Parameters = 15,
    TagStatus = 16,
    Lists = 17,

    LastMovementDate = 18,
    TotalAmount = 19,
    SingleItemPrice = 20,
    FinancialBurden = 21,
    Count = 22,
    Storages = 23,
    MovementTypes = 24,
    Notes = 25,
    StorageItemNames = 26,
    ProjectionRoles = 27,
    Owner = 28,
    SpecialRules = 29
}

export enum SelectionFilterSendConnector {
    Immutable = 0,
    Or = 1,
    And = 2
}

export enum SelectionProjectStageFilterSendType {
    // Сейчас на этапе.
    NowOnStage = 0,
    // Переведен на этап в указанные временные рамки.
    StageSetInTimeRange = 1
}

export enum StorageFilterTypeFlagsDto {
    None = 0,
    Receiver = 1,
    Source = 2,
}

export enum SelectionFileAttachmentFilter {
    None = 0,
    Client = 1,
    Project = 2,
    // Both = 3
}

export enum JobStatusFilterFlags {
    // Фильтр пуст.
    Nothing = 0,
    // Работа является записью.
    Standard = 1,
    // В работе.
    Working = 2,
    // Ожидает проверки.
    WaitingForCheck = 4,
    // Выполнена.
    Completed = 8,
    // В работе и просрочена.
    Lost = 16
}


export enum NumberFilterCompareTypeDto {
    Equals = 0,
    More = 1,
    Less = 2
}

export enum MovementTypeFilterFlagsDto {
    None = 0,
    Income = 1,
    Expense = 2,
    Transfer = 4
}

export enum TagStatusFilterFlags {
    Nothing = 0,
    Standard = 1,
    Hidden = 1 << 1, //2
    // Both = Standard | Hidden //3
}

export enum FilterConnectorType {
    Or = 0,
    And = 1
}

export enum FormulaInFilterCompareType {
    Null = 0,
    Equal = 1,
    Less = 2,
    More = 3
}

// Дескриптор типа формульного токена.
export enum AnalyticsFormulaTokenDescriptor {
    Operation = 0, // Токен - операция над аргументами. 
    Operand = 1, // Константный операнд.
    LinkToParameter = 2, // Ссылка на параметр.
    LeftBracket = 3, // Открывающая круглая скобка.
    RightBracket = 4, // Закрывающая круглая скобка.
    LinkToFormula = 5, // Ссылка на иную формулу (Только для формул к результирующей таблице).
    TotalResultsCount = 6, // Общее количество элементов в таблице (Только для формул к результирующей таблице).
    GlobalLinkToTableFormula = 7 // Ссылка на любую табличную формулу в системе (Только для формул в графиках).
}

export enum AnalyticsFormulaOperationType {
    Plus = 0,
    Minus = 1,
    Divide = 2,
    Multiply = 3,
}

export enum ParameterLinkSummablePartType {
    FirstElement = 0, // Первый / единственный элемент списка.
    ElementsSum = 1, // Сумма по всем элементам списка.
}

// Charts

export enum ChartSettingsBaseDescriptor {
    ClientLinearChart = 0,
    ClientPieChart = 1,
    ClientBarChart = 2,

    ProjectLinearChart = 3,
    ProjectPieChart = 4,
    ProjectBarChart = 5,

    JobLinearChart = 6,
    JobPieChart = 7,
    JobBarChart = 8,

    FileLinearChart = 9,
    FilePieChart = 10,
    FileBarChart = 11,

    PersonLinearChart = 12,
    PersonPieChart = 13,
    PersonBarChart = 14,

    WorkloadLinearChart = 15,
    WorkloadPieChart = 16,
    WorkloadBarChart = 17,

    StorageItemLinearChart = 18,
    StorageItemPieChart = 19,
    StorageItemBarChart = 20,

    StorageItemMovementLinearChart = 21,
    StorageItemMovementPieChart = 22,
    StorageItemMovementBarChart = 23
}

export enum LinearChartFunction {
    Absolute = 0,
    Incremental = 1
}

export enum LinearChartStepType {
    Hour = 5,
    Day = 0,
    Week = 4,
    Month = 1,
    Quarter = 2,
    Year = 3
}

export enum PieChartBundleDisplayType {
    Horizontal = 0,
    Vertical = 1
}

export enum AnalyticsOrderingType {
    NoOrder = 0,
    Ascending = 1,
    Descending = 2
}

export enum BarChartDisplayType {
    Biggest = 0,
    Smallest = 1
}

export enum ClientProjectChartFunction {
    // Количество элементов, подходящих под условие.
    Count = 0,
    // Сумма значений параметра элементов, подходящих под условие.
    ParameterSum = 1,
    // Подсчет определенных в графике формул (без привязки к элементам).
    Formulas = 2
}

export enum StorageItemChartEvaluateFunction {
    // Кол-во уникальных позиций.
    ItemsCount = 0,
    // Кол-во позиций на складах.
    ItemsInStockQuantity = 1,
    // Общая стоимость позиций на складах.
    ItemsInStockTotalAmount = 2,
    // Финансовая нагрузка.
    FinancialBurden = 3
}

export enum StorageItemMovementChartEvaluateFunction {
    MovementsCount = 0,
    ItemsQuantity = 1,
    TotalAmount = 2
}

export enum StorageItemChartSplitType {
    // Поле названия поз склада
    ItemNameField = 0,
    // Склад, на котором позиция лежит.
    Storage = 1,
    // Общая сумма позиций на складах.
    TotalAmount = 2,
    // Кол-во позиций на складах.
    ItemsQuantity = 3,
    // Финансовая нагрузка.
    FinancialBurden = 4,
    // Создатель.
    Creator = 5,
    // Цена позиции
    SingleItemPrice = 6
}

export enum StorageItemMovementChartSplitType {
    // Поле названия поз склада
    ItemNameField = 0,
    // Склад, куда поступило
    ReceiverStorage = 1,
    // Склад, откуда ушло
    SourceStorage = 2,
    // Общая сумма движения.
    TotalAmount = 3,
    // Кол-во позиций в движении.
    ItemsQuantity = 4,
    // Цена позиции в движении.
    SingleItemPrice = 5,
    // Создатель
    Creator = 6
}

export enum ParameterForChartType {
    Int = 0,
    Decimal = 1,
    Formula = 2,
    Bool = 3,

    ValueSetInt = 4,
    ValueSetDecimal = 5,
    ValueSetDate = 6,
    ValueSetString = 7,

    Record = 8,
}

export enum ProjectPieChartSplitType {
    Stages = 0,
    AddressInRole = 1,
    WorkerInRole = 2,
    Tags = 3,
    Parameter = 4,
    Owner = 5
}

export enum ProjectBarChartSplitType {
    NoSplit = 0,
    Stages = 1,
    AddressInRole = 2,
    WorkerInRole = 3,
    Tags = 4,
    Parameter = 5
}

export enum ClientPieChartSplitType {
    Address = 0,
    WorkerInRole = 1,
    Tags = 2,
    Parameter = 3,
    Owner = 4
}

export enum ClientBarChartSplitType {
    NoSplit = 0,
    Address = 1,
    WorkerInRole = 2,
    Tags = 3,
    Parameter = 4
}

export enum JobPieChartSplitType {
    AddressInRole = 0,
    WorkerInRole = 1,
    Tags = 2,
    Status = 3
}

export enum JobBarChartSplitType {
    NoSplit = 0,
    AddressInRole = 1,
    WorkerInRole = 2,
    Tags = 3,
    Status = 4
}

export enum JobWorkerRoleFlags {
    None = 0,
    Creator = 1,
    JobWorker = 1 << 1, // 2
    // Any = Creator | JobWorker // 3
}

export enum FilePieChartSplitType {
    AddressInRole = 0,
    WorkerInRole = 1,
    Tags = 2
}

export enum FileBarChartSplitType {
    NoSplit = 0,
    AddressInRole = 1,
    WorkerInRole = 2,
    Tags = 3,
    Date = 4
}

export enum FileBarChartDateMode {
    CreateDate = 0,
    LastChangeDate = 1
}

export enum FileLinearChartFactFlags {
    FileExists = 0,
    FileCreated = 1,
    FileChanged = 2, // 2
}

export enum PersonFileWorkerRoleFlags {
     None = 0, 
     Creator = 1, 
     LastEditor = 2, // 2
     // Both = Creator | LastEditor // 3
}

export enum PersonLinearChartFactFlags {
    PersonExists = 0,
    PersonCreated = 1,
    PersonChanged = 2, // 2
    PersonAttachedToProject = 4 // 4
}

export enum PersonPieChartSplitType {
    Address = 0,
    WorkerInRole = 1,
    Tags = 2,
    ParticipationInProjects = 3
}

export enum PersonBarChartSplitType {
    NoSplit = 0,
    Address = 1,
    WorkerInRole = 2,
    Tags = 3,
    ParticipationInProjects = 4
}

// Result table

export enum TableDataCellDisplayType {
    Simple = 0,
    EntityAsWorker = 1,
    Parameter = 2,
    EntityAsClientLink = 3,
    EntityAsProjectLink = 4,
    JobText = 5,
    SimpleAsTag = 6,
    LastChangeDate = 7,
    EntityAsAddress = 8,
    JobStatus = 9,
    TagStatus = 10,
    EntityAsFileLink = 11,

    // TODO: add these to query table
    MovementsOfItem = 12,
    ItemQuantityAndMeasureUnit = 13,
    ItemsAtStorages = 14,
    MovementType = 15,
    DataOfStorageItem = 16,
    EntityAsStorage = 17,
    Bill = 18,
    IdList = 19
}

export enum CellDataIdListEntityType {
    StorageItemMovements = 0,
    Jobs = 1,
    Persons = 2,
    StorageItems = 3
}

export enum JobStatus {
    Standard = 0, // Работа является записью.
    Working = 1, // В работе.
    WaitingForCheck = 2, // Ожидает проверки.
    Completed = 3, // Выполнена.
    Lost = 4 // В работе и просрочена.
}

export enum CellDataJobStatusDisplayType {
    AsColour = 1,
    AsText = 2,
    Both = 3
}

export enum AdditionToJobType {
    Ending = 0,
    Reworking
}

export enum PrimitiveCellValueType {
    String = 0, 
    Number = 1, 
    Date = 2, 
    DateTime = 3,
    Boolean = 4
}

export enum TableHeaderCellOrderMode {
    NotAllowed = 0,
    NotOrdered = 1,
    Ascending = 2,
    Descending = 3
}

// Тип данных колонки. От него зависит, какой значок сортировки рисуем в шапке
// (символьный, числовой, логический). Неизвестные типы (General, DateTime) рисуем
// обычным двухстрелочным значком.
export enum TableHeaderCellDataType {
    General = 0,
    String = 1,
    Number = 2,
    Boolean = 3,
    DateTime = 4
}

export enum AnalyticsLinearChartCurveCalculateStatus {
    Ok = 0,
    AnalyzedElementInvalid = 1,
    EvaluationParameterInvalid = 2,
    EmptyAnalyzedElementResult = 3
}

export enum AnalyticsChartSectorCalculateStatus {
    Ok = 0,

    AnalyzedElementInvalid = 1,
    AddressLevelInvalid = 2,
    WorkerRolesInvalid = 3,

    SplittingParameterInvalid = 4,
    BordersForNumberParameterInvalid = 5,
    EmptyAnalyzedElementResult = 6
}

// Ориентация отображения полей записи.
export enum V2RecordAxisType {
    // Горизонтальное отображение.
    Horizontal = 0,
    // Вертикальное отображение.
    Vertical = 1
}

// STORAGE

export enum V2StorageItemMovementType {
    // Приход товара.
    In = 0,
    // Расход товара.
    Out = 1,
    // Перемещение товара (между складами системы)
    Inner = 2
}

export enum StorageItemPriceType {
    // По последнему приходу.
    LastIncome = 0,
    // Средневзвешенная по всем приходам.
    AverageIncome = 1,
    // Устанавливается пользователем.
    SetByUser = 2
}

// Выбранный тип отображения значения поля.
export enum V2FieldValueDisplayType {
    // Отображается в отдельной строке под названием поля.
    UnderHeader = 0,
    // Отображается в той же строке рядом с названием поля.
    Inline = 1
}

export enum StorageRules {
    // Запрещено всем.
    ForbiddenToAll = 0,
    // Можно всем.
    AllowedToAll = 1,
    // Можно конкретным пользователям.
    AllowedToCertainWorkers = 2
}

export enum BillConnectionWithValuesTypeDescriptor
{
    Unspecified = 0,
    Income = 1,
    Project = 2
}

// 1C

export enum BillLinkedTypeDescriptor {
    Unspecified = 0,
    BillWithOneLink = 1,
    BillWithManyLinks = 2,
    Payment = 3
}

export enum BillSearchedTypeDescriptor {
    Unspecified = 0,
    BillWithConnections = 1,
    Payment = 2
}

export enum BillType {
    // Входящий
    Incoming = 0,
    // Исходящий
    Outgoing = 1
}

export enum BillsAndPaymentsSearchType {
    NewEntity = 0,
    Movement = 1,
    Project = 2
}

// Дескриптор конкретного типа StorageItemNameInstance.
export enum StorageItemNameInstanceDescriptor
{
    // Для поля элементарного типа
    Simple = 0,
    // Для поля типа "Запись" 
    Record = 1,
    // Для списка полей.
    Title = 2
}

export enum PayStatus {
    // Отменен.
    Cancelled = 0,
    // Не оплачен.
    NotPayed = 1,
    // Оплачен частично
    PartlyPayed = 2,
    // Оплачен полностью
    Payed = 3,
    // Статус неизвестен.
    Unknown = 4
}

export enum AnalyticsElementOptionsDescriptor {
    DisplayItemsFromParentOnly = 0,
    DisplayItemsSumAndCount = 1,
    DisplayItemsPassedFiltersOnly = 2
}

export enum DisplayItemsSumAndCountKindDto {
    // Отдельно по каждой позиции.
    EveryItemSeparated = 0,

    // Σ сумма/кол-во позиций, показанных в таблице.
    SumItemsFromTableOnly = 1,

    // Σ сумма/кол-во всех позиций действия.
    SumAllItemsOfMovement = 2
}
